/**
 * 盟主趋势工具函数
 */
import moment from 'moment'

// 盟主资金状态枚举
export const FundStatus = {
    BULL_STRONG: 1,      // 多头强势
    BULL_MODERATE: 2,    // 多头偏强
    BEAR_STRONG: 3,      // 空头强势
    BEAR_MODERATE: 4     // 空头偏强
} as const

// 盟主资金状态显示文本
export const FundStatusText: Record<number, string> = {
    1: '多头强势',
    2: '多头偏强',
    3: '空头强势',
    4: '空头偏强'
}

// 盟主资金状态颜色配置
export const FundStatusColors = {
    light: {
        bull: '#fe2f32',    // 多头（红色）
        bear: '#00a622'     // 空头（绿色）
    },
    dark: {
        bull: '#ff5c5c',    // 多头（红色）
        bear: '#39e365'     // 空头（绿色）
    }
}

// 盟主资金状态排序优先级（数值越小优先级越高）
export const FundStatusPriority: Record<number, number> = {
    1: 1,  // 多头强势 - 最高优先级
    2: 2,  // 多头偏强
    3: 3,  // 空头强势
    4: 4   // 空头偏强 - 最低优先级
}

export interface FundStatusDisplayResult {
    text: string
    color: string
}

/**
 * 根据 fundStatus 获取显示文本和颜色
 * @param fundStatus 资金状态值 (1-4)
 * @param theme 主题 'light' | 'dark'
 * @returns 显示文本和颜色
 */
export function getFundStatusDisplay(fundStatus: number | undefined | null, theme: 'light' | 'dark' = 'light'): FundStatusDisplayResult {
    const colors = FundStatusColors[theme]

    if (fundStatus === undefined || fundStatus === null) {
        return { text: '', color: '' }
    }

    switch (fundStatus) {
        case FundStatus.BULL_STRONG:
            return { text: FundStatusText[1], color: colors.bull }
        case FundStatus.BULL_MODERATE:
            return { text: FundStatusText[2], color: colors.bull }
        case FundStatus.BEAR_STRONG:
            return { text: FundStatusText[3], color: colors.bear }
        case FundStatus.BEAR_MODERATE:
            return { text: FundStatusText[4], color: colors.bear }
        default:
            return { text: '', color: '' }
    }
}

/**
 * 比较两个 fundStatus 的排序优先级
 * 排序优先级：多头强势 > 多头偏强 > 空头强势 > 空头偏强
 * @param a 第一个 fundStatus
 * @param b 第二个 fundStatus
 * @returns 负数表示 a 优先级更高，正数表示 b 优先级更高，0 表示相等
 */
export function compareFundStatus(a: number | undefined | null, b: number | undefined | null): number {
    const priorityA = a !== undefined && a !== null ? (FundStatusPriority[a] ?? 999) : 999
    const priorityB = b !== undefined && b !== null ? (FundStatusPriority[b] ?? 999) : 999
    return priorityA - priorityB
}

/**
 * 格式化日期，空值返回 "—"
 * @param timestamp 时间戳（毫秒）
 * @returns 格式化后的日期字符串 YYYY-MM-DD 或 "—"
 */
export function formatMengzhuDate(timestamp: number | undefined | null): string {
    if (!timestamp || timestamp === 0) {
        return '—'
    }
    return moment(timestamp).format('YYYY-MM-DD')
}

/**
 * 格式化盟主趋势更新时间（与四维猎手格式一致）
 * @param updateTime 更新时间戳（毫秒）
 * @returns 格式化后的时间字符串
 */
export function formatMengzhuUpdateTime(updateTime: number | undefined | null): string {
    if (!updateTime) return '--'
    return moment(updateTime).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * 根据涨跌幅获取颜色
 * @param value 涨跌幅数值
 * @param theme 主题 'light' | 'dark'
 * @returns 颜色值
 */
export function getChangeColor(value: number | undefined | null, theme: 'light' | 'dark' = 'light'): string {
    const colors = FundStatusColors[theme]

    if (value === undefined || value === null) {
        return ''
    }

    if (value > 0) {
        return colors.bull  // 红色
    } else if (value < 0) {
        return colors.bear  // 绿色
    }
    return ''  // 零值返回默认色
}

/**
 * 验证 fundStatus 是否有效
 * @param fundStatus 资金状态值
 * @returns 是否有效
 */
export function isValidFundStatus(fundStatus: number): boolean {
    return [1, 2, 3, 4].includes(fundStatus)
}

export default {
    FundStatus,
    FundStatusText,
    FundStatusColors,
    FundStatusPriority,
    getFundStatusDisplay,
    compareFundStatus,
    formatMengzhuDate,
    formatMengzhuUpdateTime,
    getChangeColor,
    isValidFundStatus
}
